/**
 * Belote's two bid layouts, both generated from `legalCalls`.
 *
 * Classic is a question about one card: the turned card sits in the middle,
 * and the choice is the suits you may take it in — the turned suit in round 1,
 * the other three in round 2. Coinche is a printed table like 500's: values
 * down, suits across, Capot along the bottom, and the coinche beside it when it
 * is yours to say.
 */
import { useState } from 'react'
import type { CardId, Suit } from '@cards/shared'
import type { BidLayoutProps, LegalCall } from '../types'
import { Glyph } from '../../table/atoms'
import { MiniCard } from '../../table/Card'
import { useSettings } from '../../settings/settings'

const SUITS: readonly Suit[] = ['S', 'H', 'D', 'C']
const VALUES: readonly number[] = [80, 90, 100, 110, 120, 130, 140, 150, 160]

const isRecord = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null

export function readUpcard(auction: unknown): CardId | null {
  return isRecord(auction) && typeof auction.upcard === 'string' ? (auction.upcard as CardId) : null
}

function cellKey(call: unknown): string | null {
  if (!isRecord(call)) return null
  if (call.type === 'bid') return `${call.value}${call.suit}`
  if (call.type === 'capot') return `capot${call.suit}`
  if (call.type === 'take') return `take${call.suit}`
  if (call.type === 'coinche' || call.type === 'surcoinche') return call.type
  return null
}

export function ClassicBids({ view, calls, selected, onSelect }: BidLayoutProps): JSX.Element {
  const [settings] = useSettings()
  const upcard = readUpcard(view.hand?.auction)
  const round = isRecord(view.hand?.auction) ? view.hand?.auction.round : 1
  return (
    <div className="belote-take">
      {upcard ? (
        <div className="upcard">
          <MiniCard card={upcard} deck={settings.deck} />
          <small>{round === 2 ? 'turned, not on offer' : 'turned card'}</small>
        </div>
      ) : null}
      <div className="call-list" style={{ gridTemplateColumns: `repeat(${Math.max(calls.length, 1)}, 88px)`, justifyContent: 'center' }}>
        {calls.map((call) => {
          const suit = isRecord(call.call) ? (call.call.suit as Suit) : null
          const sel = selected?.key === call.key
          return (
            <button
              key={call.key}
              type="button"
              className={`cell${sel ? ' sel' : ''}`}
              onClick={() => onSelect(sel ? null : call)}
              aria-pressed={sel}
            >
              {suit ? <Glyph suit={suit} /> : call.label}
              <small>take</small>
            </button>
          )
        })}
      </div>
    </div>
  )
}

const ROWS = 4

export function CoincheBids({ calls, selected, onSelect }: BidLayoutProps): JSX.Element {
  const [page, setPage] = useState(0)
  const live = new Map<string, LegalCall>()
  for (const call of calls) {
    const key = cellKey(call.call)
    if (key) live.set(key, call)
  }
  const selectedKey = selected ? cellKey(selected.call) : null
  const cell = (key: string, label: React.ReactNode) => {
    const call = live.get(key)
    const sel = selectedKey === key
    return (
      <button
        key={key}
        type="button"
        className={`cell${call ? '' : ' dead'}${sel ? ' sel' : ''}`}
        disabled={!call}
        onClick={() => onSelect(sel ? null : (call ?? null))}
        aria-pressed={sel}
      >
        {label}
      </button>
    )
  }
  // Only the rows still reachable — below the standing bid is dead print —
  // and four at a time, because nine rows would push the table off a phone.
  const lowest = VALUES.find((v) => SUITS.some((s) => live.has(`${v}${s}`)))
  const reachable = lowest === undefined ? [] : VALUES.filter((v) => v >= lowest)
  const pages = Math.max(1, Math.ceil(reachable.length / ROWS))
  const shown = Math.min(page, pages - 1)
  const rows = reachable.slice(shown * ROWS, shown * ROWS + ROWS)
  const doubling = live.get('surcoinche') ?? live.get('coinche') ?? null
  return (
    <div>
      {rows.length > 0 ? (
        <div className="ladder" style={{ gridTemplateColumns: '34px repeat(4, 1fr)' }}>
          <div className="hd" />
          {SUITS.map((s) => (
            <div key={s} className="hd"><Glyph suit={s} /></div>
          ))}
          {rows.map((v) => (
            <Row key={v} value={v}>{SUITS.map((s) => cell(`${v}${s}`, v))}</Row>
          ))}
        </div>
      ) : null}
      {pages > 1 ? (
        <button type="button" className="btn btn-quiet btn-block" style={{ height: 30 }} onClick={() => setPage((shown + 1) % pages)}>
          {shown + 1 < pages ? `Higher: ${reachable[(shown + 1) * ROWS]} and up` : `Back to ${reachable[0]}`}
        </button>
      ) : null}
      <div className="ladder-misere" style={{ gridTemplateColumns: doubling ? '1fr 1fr' : '1fr', paddingLeft: 34 }}>
        {SUITS.some((s) => live.has(`capot${s}`)) ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {SUITS.map((s) => cell(`capot${s}`, <>Capot <Glyph suit={s} /></>))}
          </div>
        ) : null}
        {doubling ? cell(cellKey(doubling.call)!, doubling.label) : null}
      </div>
    </div>
  )
}

function Row({ value, children }: { value: number; children: React.ReactNode }): JSX.Element {
  return (
    <>
      <div className="lv">{value}</div>
      {children}
    </>
  )
}
